import { format } from "date-fns";
import { RacePlan } from "./dategrid";
import { render } from "./rendering";
import { Units } from "types/app";

/**
 * Converts a training plan to Garmin Connect-compatible format
 * Garmin accepts structured workout data in JSON format
 * This creates a file that can be imported into Garmin Connect
 */
export function toGarmin(plan: RacePlan, units: Units): string | undefined {
  const workouts: any[] = [];
  const weeks = plan.dateGrid.weeks;

  for (let i = 0; i < weeks.length; i++) {
    const currWeek = weeks[i];

    for (let j = 0; j < currWeek.days.length; j++) {
      const currWorkout = currWeek.days[j];

      if (currWorkout.event) {
        const [title, desc] = render(
          currWorkout.event,
          plan.sourceUnits,
          units,
        );
        const workoutType = currWorkout.event.type || "Run";

        // Parse distance from event
        let distance = 0;
        if (currWorkout.event.dist !== undefined) {
          if (typeof currWorkout.event.dist === "number") {
            distance = currWorkout.event.dist;
          } else if (
            Array.isArray(currWorkout.event.dist) &&
            currWorkout.event.dist.length > 0
          ) {
            distance = currWorkout.event.dist[0];
          }
        }

        // Convert distance to meters (Garmin uses metric)
        let distanceInMeters = distance;
        if (units === "mi") {
          distanceInMeters = distance * 1609.34;
        } else {
          distanceInMeters = distance * 1000;
        }

        const workout = {
          workoutName: title,
          description: desc || title,
          sport: "running",
          scheduledDate: format(currWorkout.date, "yyyy-MM-dd"),
          workoutType: workoutType.toUpperCase(),
          estimatedDurationInSeconds: Math.round(distanceInMeters / 2.5), // ~6:00/km pace estimate
          estimatedDistanceInMeters: Math.round(distanceInMeters),
          steps: [
            {
              type: "WorkoutStep",
              stepOrder: 1,
              intensity:
                workoutType === "Race"
                  ? "ACTIVE"
                  : workoutType === "Easy"
                    ? "WARMUP"
                    : "ACTIVE",
              durationType: "DISTANCE",
              durationValue: Math.round(distanceInMeters),
              targetType: "OPEN",
              description: desc || title,
            },
          ],
        };

        workouts.push(workout);
      }
    }
  }

  const garminData = {
    trainingPlan: {
      planName: `${plan.raceType} Training Plan`,
      description: `Training plan for ${plan.raceType}. Generated by ProgressPlans.`,
      startDate: format(plan.planDates.planStartDate, "yyyy-MM-dd"),
      endDate: format(plan.planDates.planEndDate, "yyyy-MM-dd"),
      workouts: workouts,
    },
  };

  return JSON.stringify(garminData, null, 2);
}
